import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/58ujAQtekwQT37CqH9LFPA/prime_nation_transparent_bg.png';
interface NavItem {
  name: string;
  href: string;
  children?: {
    name: string;
    href: string;
  }[];
}
const navLinks: NavItem[] = [{
  name: 'ABOUT',
  href: '#about',
  children: [{
    name: 'Members',
    href: '#members'
  }]
}, {
  name: 'WORK WITH US',
  href: '#work-with-us'
}, {
  name: 'SERVICES',
  href: '#services',
  children: [{
    name: 'Do I Qualify',
    href: '#qualify'
  }, {
    name: 'Schedule Appointment',
    href: '#contact'
  }]
}, {
  name: 'GALLERY',
  href: '#gallery'
}, {
  name: 'PRESS & NEWS',
  href: '#press'
}, {
  name: 'CONTACT',
  href: '#contact',
  children: [{
    name: 'Locations',
    href: '#locations'
  }]
}];
function DesktopDropdown({
  item
}: {
  item: NavItem;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };
  if (!item.children) {
    return <a href={item.href} className="text-brand-soft font-mono text-[13px] uppercase tracking-widest hover:text-brand-lime transition-colors" data-id="element-103">
        {item.name}
      </a>;
  }
  return <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave} data-id="element-104">
      <a href={item.href} className="text-brand-soft font-mono text-[13px] uppercase tracking-widest hover:text-brand-lime transition-colors flex items-center gap-1" data-id="element-105">
        {item.name}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-106" />
      </a>

      <AnimatePresence data-id="element-107">
        {open && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="absolute top-full left-0 mt-2 bg-brand-offblack border border-brand-concrete shadow-xl min-w-[200px] py-2 z-50" data-id="element-108">
            {item.children.map(child => <a key={child.name} href={child.href} className="block px-5 py-3 text-brand-soft/80 font-mono text-[13px] uppercase tracking-widest hover:text-brand-lime hover:bg-brand-concrete/50 transition-colors" data-id="element-109">
                {child.name}
              </a>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MobileNavItem({
  item,
  onClose
}: {
  item: NavItem;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!item.children) {
    return <a href={item.href} onClick={onClose} className="flex items-center px-6 py-4 text-brand-soft font-mono text-sm uppercase tracking-widest hover:text-brand-lime transition-colors border-b border-brand-concrete" data-id="element-110">
        {item.name}
      </a>;
  }
  return <div className="border-b border-brand-concrete" data-id="element-111">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full px-6 py-4 text-brand-soft font-mono text-sm uppercase tracking-widest hover:text-brand-lime transition-colors min-h-[44px]" data-id="element-112">
        {item.name}
        <ChevronDown size={18} className={`text-brand-lime transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-113" />
      </button>

      <AnimatePresence data-id="element-114">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-115">
            <div className="pl-10 pb-3 flex flex-col bg-brand-offblack" data-id="element-116">
              {item.children.map(child => <a key={child.name} href={child.href} onClick={onClose} className="text-brand-soft/60 font-mono text-sm uppercase tracking-widest hover:text-brand-lime transition-colors py-3 flex items-center min-h-[44px]" data-id="element-117">
                  {child.name}
                </a>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled ? 'bg-brand-black shadow-lg' : 'bg-brand-black/90'}`} data-id="element-118">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-119">
          <div className="flex justify-between items-center h-[60px] md:h-[64px]" data-id="element-120">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2 shrink-0" data-id="element-121">
              <img src={LOGO_URL} alt="Prime Nation Logo" className="h-10 md:h-12 w-auto object-contain brightness-0 invert" data-id="element-122" />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6" data-id="element-123">
              {navLinks.map(link => <DesktopDropdown key={link.name} item={link} data-id="element-124" />)}
              <button className="bg-brand-lime text-brand-black px-6 py-2.5 font-mono text-[13px] uppercase tracking-widest font-bold hover:bg-white transition-colors min-h-[44px]" data-id="element-125">
                Apply Now
              </button>
            </nav>

            {/* Mobile Menu Toggle */}
            <button className="lg:hidden text-brand-soft p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setIsOpen(true)} aria-label="Open menu" data-id="element-126">
              <Menu size={28} data-id="element-127" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Overlay — outside header to avoid backdrop-filter stacking context issues */}
      <AnimatePresence data-id="element-128">
        {isOpen && <motion.div initial={{
        opacity: 1
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-[60] bg-[#0A0A0A] overflow-y-auto lg:hidden" data-id="element-129">
            {/* Header bar */}
            <div className="flex justify-between items-center h-[60px] px-4 bg-[#0A0A0A]" data-id="element-130">
              <a href="#" className="flex items-center gap-2 shrink-0" onClick={() => setIsOpen(false)} data-id="element-131">
                <img src={LOGO_URL} alt="Prime Nation Logo" className="h-10 w-auto object-contain brightness-0 invert" data-id="element-132" />
              </a>
              <button onClick={() => setIsOpen(false)} className="text-brand-soft p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close menu" data-id="element-133">
                <X size={28} data-id="element-134" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex flex-col pt-4" data-id="element-135">
              {navLinks.map(link => <MobileNavItem key={link.name} item={link} onClose={() => setIsOpen(false)} data-id="element-136" />)}
              <div className="px-6 pt-8 pb-6" data-id="element-137">
                <button className="bg-brand-lime text-brand-black w-full py-4 font-mono text-sm uppercase tracking-widest font-bold min-h-[52px] hover:bg-white transition-colors" data-id="element-138">
                  Apply Now
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}